import { MarkerType } from '@ikegps/core-api';
import type { Point } from '@ikegps/core-api';
import { CollectionModelDrawableMarker } from '../../model/collection/drawable/marker.drawable';
import type { CollectionModelDrawableMarkerOffset } from '../../model/collection/drawable/marker.offset.drawable';
import type { SVGRenderOptions } from '../draw/svg/svg.render';
import { m } from '../mithril';
import { ClassNames, StyleConfig } from '../theme';
import { LabelSVGDraw } from './label.draw.svg';
import { SVGUtil } from './util.draw.svg';
import { IkeDrawLabel } from './util.draw.svg';

const MARKER_CIRCLE_RADIUS = 6;

export class MarkerSVGDraw {
	static view(marker: CollectionModelDrawableMarker | CollectionModelDrawableMarkerOffset, options: SVGRenderOptions) {
		// If the current element is an insight one, but we don't have the ability to view them, then don't render it.
		if (marker.isInsightMeasurement) {
			if (!options.isInsightMarkersVisible) {
				return null;
			}
			if (
				marker.isApprovableInsightDrawable() &&
				!(options.insightMarkerTypesVisible ?? []).includes(marker.insight?.detection_class ?? '')
			) {
				return null;
			}
		}

		if (!marker.isVisible) {
			return null;
		}

		const output: unknown[] = [];

		// If there is a label, draw it.
		if (marker.label) {
			output.push(LabelSVGDraw.view(marker.label, options));
		}

		const startPoint = { x: marker.x - options.draw.state.markerOffsetWidth, y: marker.y };
		const endPoint = { x: marker.x + options.draw.state.markerOffsetWidth, y: marker.y };
		const isMoveable = options.isInteractive && marker.type !== MarkerType.BASE_OFFSET;

		// Draw a line for the marker itself on the center of the pole.
		output.push(SVGUtil.DrawLineMain(startPoint, endPoint, options.scale));
		output.push(
			SVGUtil.DrawLineSecondary(
				startPoint,
				endPoint,
				options.scale,
				marker.isInsightMeasurement ? StyleConfig.STOKE_SECONDARY_INSIGHT : StyleConfig.STROKE_SECONDARY,
				isMoveable ? ClassNames.isMoveable : undefined
			)
		);

		// If the marker is draggable, draw a transparent "move line" (essentially a rectangle)
		// over the marker for user interaction.
		if (options.isInteractive) {
			if (CollectionModelDrawableMarker.isMarker(marker) && marker.pair) {
				if (options.isVerticalSeparationEnabled) {
					output.push(SVGUtil.DrawMoveLine(startPoint, endPoint, options.scale));
				}
			} else {
				output.push(SVGUtil.DrawMoveLine(startPoint, endPoint, options.scale));
			}
		}

		output.push(MarkerSVGDraw.DrawHandle(marker, options));

		const isSelected = options.draw.state.selectedId === marker.id;
		const isHighlighted = options.draw.state.hoverId === marker.id;

		const classNames = [
			ClassNames.marker.container,
			marker.type === MarkerType.BASE ? ClassNames.marker.isBase : null,
			marker.type === MarkerType.BASE_OFFSET ? ClassNames.marker.isBaseOffset : null,

			isSelected ? ClassNames.isSelected : null,
			isHighlighted ? ClassNames.isHighlighted : null,
		];

		return m(
			'g',
			{
				key: marker.id,
				className: classNames.join(' '),
				'c-Draw-node-id': marker.id,
				'c-Draw-tag': 'marker',
			},
			output
		);
	}

	private static DrawHandle(
		marker: CollectionModelDrawableMarker | CollectionModelDrawableMarkerOffset,
		options: SVGRenderOptions
	) {
		const isSelected = options.draw.state.selectedId === marker.id;
		const isHighlighted = options.draw.state.hoverId === marker.id;
		const isMoveable = options.isInteractive && marker.type !== MarkerType.BASE_OFFSET;

		const pointClassNames = [
			isSelected ? ClassNames.isSelected : null,
			isSelected ? ClassNames.point.isSelected : null,
			isHighlighted ? ClassNames.point.isHighlighted : null,
			isMoveable ? ClassNames.isMoveable : null,
		];

		// Styles for hover and select state
		if (options.isInteractive && (isSelected || isHighlighted)) {
			var pointFillColor = StyleConfig.LABEL.FILL;
			var pointStrokeColor = StyleConfig.LABEL.STROKE;

			if (marker.isInsightMeasurement) {
				pointFillColor = StyleConfig.LABEL.FILL_INSIGHT;
				if (isHighlighted) {
					pointStrokeColor = StyleConfig.LABEL.FILL_INSIGHT;
				} else {
					pointStrokeColor = StyleConfig.STROKE;
				}
			} else {
				pointFillColor = StyleConfig.LABEL.FILL;
				if (isHighlighted) {
					pointFillColor = StyleConfig.LABEL.FILL_HIGHLIGHTED;
				} else if (isSelected) {
					pointFillColor = StyleConfig.LABEL.FILL_SELECTED;
					pointStrokeColor = StyleConfig.STROKE;
				}
			}

			let pointDraggable = marker.type !== MarkerType.BASE_OFFSET;
			// normal user can't drag open space
			if (CollectionModelDrawableMarker.isMarker(marker) && marker.pair && !options.isVerticalSeparationEnabled) {
				pointDraggable = false;
			}

			// If label is on the left, draw the point on the left.
			// Otherwise, draw the point on the right.
			const point: Point = {
				x: marker.label.isOnLeftSide()
					? marker.x - options.draw.state.markerOffsetWidth // Left
					: marker.x + options.draw.state.markerOffsetWidth, // Right
				y: marker.y,
			};

			return SVGUtil.DrawPoint(point, {
				className: pointClassNames.join(' '),
				radius: options.scale * MARKER_CIRCLE_RADIUS,
				scale: options.scale,
				fill: pointFillColor,
				stroke: pointStrokeColor,
				draggable: pointDraggable,
				label: marker.type !== MarkerType.BASE_OFFSET ? IkeDrawLabel.POINT : undefined,
			});
		}
	}
}
